import { ColetaJob, ColetaVtmeLado, ColetaVtmeLinha } from '../../types/coleta';
import { chaveTelefoneBr } from './coletaResumoQualidade';

export function temIdsPedidoVtme(idAditivo?: string | null, idRenegociacao?: string | null): boolean {
  return Boolean((idAditivo || '').trim() || (idRenegociacao || '').trim());
}

export function jobVtmeComResultado(job: ColetaJob | null | undefined): boolean {
  const estado = (job?.estado || '').toLowerCase();
  return Boolean(job) && (estado === 'ok' || estado === 'erro') && job?.resultado != null;
}

export function deveEnfileirarVtme(opcoes: {
  temIds: boolean;
  temResultado: boolean;
  forcar: boolean;
  aberto?: boolean;
}): boolean {
  if (!opcoes.temIds || opcoes.aberto) return false;
  return !opcoes.temResultado || opcoes.forcar;
}

function ehAditivo(negociacao?: string | null): boolean {
  const texto = (negociacao || '').toLowerCase();
  if (texto.includes('linha nova')) return false;
  return texto.includes('aditivo') || texto.includes('cliente novo');
}

function ehRenegociacao(negociacao?: string | null): boolean {
  return (negociacao || '').toLowerCase().includes('reneg');
}

export function ladosDoContratoAberto(opcoes: {
  idAditivo?: string | null;
  idRenegociacao?: string | null;
  linhasContrato?: Array<{ negociacao?: string | null; excluirDoContrato?: boolean | null }>;
}): { aditivo: boolean; renegociacao: boolean } {
  const temIdAditivo = Boolean((opcoes.idAditivo || '').trim());
  const temIdReneg = Boolean((opcoes.idRenegociacao || '').trim());
  const linhas = (opcoes.linhasContrato || []).filter((linha) => !linha.excluirDoContrato);
  const temLinhaAditivo = linhas.some((linha) => ehAditivo(linha.negociacao));
  const temLinhaReneg = linhas.some((linha) => ehRenegociacao(linha.negociacao));
  return {
    aditivo: temIdAditivo || temLinhaAditivo,
    renegociacao: temIdReneg || temLinhaReneg,
  };
}

const CODIGOS_NUMERO = new Set([
  'vazio',
  'numero_invalido',
  'nao_encontrado',
  'acesso_sem_par',
  'ddd_diferente',
  'iccid_diferente',
  'iccid_vazio',
  'plano_diferente',
  'valor_diferente',
  'ok',
]);

const CODIGOS_DIVERGENTES = new Set([
  'vazio',
  'numero_invalido',
  'nao_encontrado',
  'acesso_sem_par',
  'ddd_diferente',
  'iccid_diferente',
  'iccid_vazio',
  'plano_diferente',
  'valor_diferente',
  'divergente',
]);

export function resumoNumeroDoLado(lado?: ColetaVtmeLado | null): string {
  const linhas = lado?.linhas || [];
  if (linhas.some((linha) => linha.resultado === 'ddd_diferente')) return 'ddd_diferente';
  if (linhas.some((linha) => linha.resultado === 'iccid_diferente' || linha.resultado === 'iccid_vazio')) {
    return 'iccid_diferente';
  }
  if (linhas.some((linha) => linha.resultado === 'acesso_sem_par' || linha.resultado === 'nao_encontrado')) {
    return 'acesso_sem_par';
  }
  if ((lado?.soVtme || []).length > 0) return 'acesso_sem_par';
  if (linhas.length > 0) return 'ok';
  return (lado?.resultado || '').trim();
}

export function textoConferenciaVtme(codigo?: string): string {
  const rotulos: Record<string, string> = {
    vazio: 'Sem número',
    numero_invalido: 'Número inválido',
    nao_encontrado: 'Não encontrado',
    acesso_sem_par: 'Não encontrado',
    ddd_diferente: 'DDD diferente',
    iccid_diferente: 'ICCID diferente',
    iccid_vazio: 'Sem ICCID',
    plano_diferente: 'Plano diferente',
    valor_diferente: 'Valor diferente',
    divergente: 'Divergente',
    ok: 'Ok',
  };
  return rotulos[codigo || ''] || 'Sem conferência';
}

export function blocoCnpjVtme(lado: ColetaVtmeLado | null | undefined, cnpjContrato: string): {
  contrato: string;
  pedido: string;
  ok: boolean | null;
} {
  const contrato = (cnpjContrato || '').replace(/\D/g, '');
  const pedidoInformado = (lado?.cnpjPedido || '').replace(/\D/g, '');
  if (pedidoInformado) {
    return {
      contrato,
      pedido: pedidoInformado,
      ok: Boolean(lado?.cnpjOk) || (lado?.cnpjOk == null && pedidoInformado === contrato),
    };
  }
  if ((lado?.resultado || '') === 'cnpj_diferente') {
    return { contrato, pedido: (lado?.detalhe || '').replace(/\D/g, ''), ok: false };
  }
  if ((lado?.linhas || []).length > 0 || (lado?.resultado || '') === 'ok') {
    return { contrato, pedido: contrato, ok: true };
  }
  return { contrato, pedido: '', ok: null };
}

export function blocoLinhasVtme(
  lado: ColetaVtmeLado | null | undefined,
  linhasContrato?: Array<{
    ddd?: string | null;
    numero?: string | null;
    chipCcid?: string | null;
    negociacao?: string | null;
    excluirDoContrato?: boolean | null;
  }>,
  ladoNome?: 'aditivo' | 'renegociacao',
): { soGettel: string[]; soVtme: string[] } {
  if (lado?.soGettel != null || lado?.soVtme != null) {
    return {
      soGettel: (lado.soGettel || []).map((item) => item.trim()).filter(Boolean),
      soVtme: (lado.soVtme || []).map((item) => item.trim()).filter(Boolean),
    };
  }
  if (ladoNome === 'aditivo') {
    const gettel = (linhasContrato || [])
      .filter((linha) => !linha.excluirDoContrato && ehAditivo(linha.negociacao))
      .map((linha) => digitos(linha.chipCcid))
      .filter(Boolean);
    const vtme = (lado?.iccidsObservacao || []).map((item) => digitos(item)).filter(Boolean);
    return {
      soGettel: unicosIccid(gettel.filter((item) => !vtme.includes(item))),
      soVtme: unicosIccid(vtme.filter((item) => !gettel.includes(item))),
    };
  }
  const gettel = (linhasContrato || [])
    .filter((linha) => !linha.excluirDoContrato)
    .filter((linha) => {
      if (ladoNome === 'renegociacao') return ehRenegociacao(linha.negociacao);
      return true;
    })
    .map((linha) => gsmDe(linha.ddd, linha.numero))
    .filter(Boolean);
  const vtme = (lado?.linhas || []).map((linha) => gsmDe(linha.ddd, linha.numero)).filter(Boolean);
  const soGettel = unicos(gettel.filter((numero) => !estaNoConjunto(numero, vtme)));
  const soVtme = unicos(vtme.filter((numero) => !estaNoConjunto(numero, gettel)));
  return { soGettel, soVtme };
}

export function linhasTabelaVtme(
  lado?: ColetaVtmeLado | null,
  linhasContrato?: Array<{
    ddd?: string | null;
    numero?: string | null;
    chipCcid?: string | null;
    negociacao?: string | null;
    excluirDoContrato?: boolean | null;
  }>,
  ladoNome?: 'aditivo' | 'renegociacao',
): Array<{
  ddd: string;
  numero: string;
  iccid: string;
  plano: string;
  valor: string;
  conferencia: string;
  divergente: boolean;
}> {
  if (ladoNome === 'aditivo') {
    return (lado?.linhas || []).map((linha) => linhaTabelaAditivo(linha));
  }
  const gettel = (linhasContrato || [])
    .filter((linha) => !linha.excluirDoContrato)
    .filter((linha) => {
      if (ladoNome === 'renegociacao') return ehRenegociacao(linha.negociacao);
      return true;
    })
    .map((linha) => gsmDe(linha.ddd, linha.numero))
    .filter(Boolean);
  return (lado?.linhas || []).map((linha) => linhaTabelaReneg(linha, gettel));
}

function linhaTabelaAditivo(linha: ColetaVtmeLinha): {
  ddd: string;
  numero: string;
  iccid: string;
  plano: string;
  valor: string;
  conferencia: string;
  divergente: boolean;
} {
  const gsm = gsmDe(linha.ddd, linha.numero);
  const ddd = (linha.ddd || '').replace(/\D/g, '') || (gsm.length >= 10 ? gsm.slice(0, 2) : '');
  const codigo = (linha.resultado || '').trim();
  const resolvido = CODIGOS_NUMERO.has(codigo) ? codigo : codigo;
  return {
    ddd: ddd || '—',
    numero: '',
    iccid: '—',
    plano: (linha.plano || '').trim() || '—',
    valor: (linha.valor || '').trim() || '—',
    conferencia: textoConferenciaVtme(resolvido),
    divergente: CODIGOS_DIVERGENTES.has(resolvido),
  };
}

function linhaTabelaReneg(linha: ColetaVtmeLinha, gettel: string[]): {
  ddd: string;
  numero: string;
  iccid: string;
  plano: string;
  valor: string;
  conferencia: string;
  divergente: boolean;
} {
  const gsm = gsmDe(linha.ddd, linha.numero);
  const ddd = (linha.ddd || '').replace(/\D/g, '') || (gsm.length >= 10 ? gsm.slice(0, 2) : '');
  const local = gsm && ddd && gsm.startsWith(ddd) ? gsm.slice(ddd.length) : ((linha.numero || '').replace(/\D/g, '') || gsm);
  const codigo = codigoNumeroVtme(linha.resultado, gsm, gettel);
  return {
    ddd: ddd || '—',
    numero: local || '—',
    iccid: '',
    plano: (linha.plano || '').trim() || '—',
    valor: (linha.valor || '').trim() || '—',
    conferencia: textoConferenciaVtme(codigo),
    divergente: CODIGOS_DIVERGENTES.has(codigo),
  };
}

function codigoNumeroVtme(resultado?: string, gsm?: string, gettel: string[] = []): string {
  const codigo = (resultado || '').trim();
  if (CODIGOS_NUMERO.has(codigo)) return codigo;
  if (!gsm) return 'vazio';
  if (estaNoConjunto(gsm, gettel)) return 'ok';
  if (gettel.some((candidato) => mesmoLocal(gsm, candidato))) return 'ddd_diferente';
  if (gettel.length > 0) return 'acesso_sem_par';
  return '';
}

function digitos(valor?: string | null): string {
  return (valor || '').replace(/\D/g, '');
}

function gsmDe(ddd?: string | null, numero?: string | null): string {
  const dddInformado = (ddd || '').replace(/\D/g, '');
  const numeroInformado = (numero || '').replace(/\D/g, '');
  if (dddInformado && numeroInformado && !numeroInformado.startsWith(dddInformado)) {
    return dddInformado + numeroInformado;
  }
  return numeroInformado || dddInformado;
}

function estaNoConjunto(numero: string, conjunto: string[]): boolean {
  const chave = chaveTelefoneBr(numero);
  if (!chave) return false;
  return conjunto.some((candidato) => chaveTelefoneBr(candidato) === chave);
}

function mesmoLocal(a: string, b: string): boolean {
  const localA = chaveTelefoneBr(a).slice(2);
  const localB = chaveTelefoneBr(b).slice(2);
  return Boolean(localA) && localA === localB;
}

function unicos(valores: string[]): string[] {
  const vistos = new Set<string>();
  const saida: string[] = [];
  valores.forEach((valor) => {
    const chave = chaveTelefoneBr(valor) || valor;
    if (vistos.has(chave)) return;
    vistos.add(chave);
    saida.push(valor);
  });
  return saida;
}

function unicosIccid(valores: string[]): string[] {
  const vistos = new Set<string>();
  const saida: string[] = [];
  valores.forEach((valor) => {
    if (vistos.has(valor)) return;
    vistos.add(valor);
    saida.push(valor);
  });
  return saida;
}
